import { LogOut, Menu } from "lucide-react";
import { Suspense, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/input";
import { auth } from "@/lib/auth";
import { useMe, useOrgPick, useOrgs } from "@/lib/hooks";
import { useBusStatus } from "@/lib/ws";
import { cn } from "@/lib/utils";
import { canEnter, ROUTES } from "@/routes";
import { zh } from "@/i18n/zh";

function WsIndicator() {
  const status = useBusStatus((s) => s.status);
  const tone = status === "open" ? "bg-success" : status === "connecting" ? "bg-warning" : "bg-destructive";
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="ws-status" data-status={status}>
      <span className={cn("size-2 rounded-full", tone)} />
      {zh.ws[status]}
    </span>
  );
}

function OrgPicker() {
  const me = useMe().data;
  const isPlatform = !!me && !me.user.org_id;
  const orgs = useOrgs(isPlatform);
  const { orgId, set } = useOrgPick();
  if (!isPlatform) return null;
  return (
    <Select value={orgId ?? ""} onChange={(e) => set(e.target.value || null)} className="h-8 max-w-48" aria-label="机构">
      <option value="">选择机构</option>
      {orgs.data?.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </Select>
  );
}

export function Shell() {
  const me = useMe();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = ROUTES.filter((r) => r.nav && canEnter(me.data, r));
  const { pathname } = useLocation();
  return (
    <div className="flex h-full">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 w-52 shrink-0 border-r bg-card transition-transform md:static md:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 items-center px-4 font-semibold">{zh.app.name}</div>
        <nav className="grid gap-0.5 px-2" aria-label="主菜单">
          {items.map((r) => {
            // 前缀相同的菜单（/robots 与 /robots/wall）只亮最长匹配的那个
            const matches = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
            const active = matches(r.path) && !items.some((o) => o.path.length > r.path.length && matches(o.path));
            const Icon = r.nav!.icon;
            return (
              <Link
                key={r.path}
                to={r.path}
                onClick={() => setOpen(false)}
                aria-current={active ? "page" : undefined}
                className={cn("flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent", active && "bg-accent font-medium text-primary")}
              >
                <Icon className="size-4" /> {r.nav!.label}
              </Link>
            );
          })}
        </nav>
      </aside>
      {open ? <div className="fixed inset-0 z-20 bg-black/30 md:hidden" onClick={() => setOpen(false)} /> : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-card px-4">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(true)} aria-label="菜单">
            <Menu />
          </Button>
          <WsIndicator />
          <div className="flex-1" />
          <OrgPicker />
          <span className="text-sm" data-testid="me-name">
            {me.data?.user.display_name}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              await auth.logout();
              nav("/login", { replace: true });
            }}
          >
            <LogOut /> {zh.nav.logout}
          </Button>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
